import React from 'react';
import "./store/store-v5.css";

interface NewsletterSignupProps {
    storeName: string;
}

export default function NewsletterSignup({ storeName }: NewsletterSignupProps) {
    const [email, setEmail] = React.useState("");
    const [status, setStatus] = React.useState<"idle" | "loading" | "success" | "error">("idle");
    const [message, setMessage] = React.useState("");
    const [mounted, setMounted] = React.useState(false);

    React.useEffect(() => {
        setMounted(true);
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) return;

        setStatus("loading");
        setMessage("");

        try {
            const res = await fetch("/api/subscribe", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, source: storeName }),
            });

            const data = await res.json();

            if (!res.ok) throw new Error(data.error || "Failed to subscribe");

            setStatus("success");
            setMessage(data.message);
            setEmail("");
        } catch (error: any) {
            setStatus("error");
            setMessage(error.message);
        }
    };

    const inputId = React.useId();
    const messageId = React.useId();

    return (
        <section className="sp-newsletter" aria-label={`${storeName} coupon alerts`}>
            <div className="sp-newsletter__text">
                <p className="sp-newsletter__title">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <rect x="3" y="5" width="18" height="14" rx="3" />
                        <path d="m4 8 8 5.5L20 8" />
                    </svg>
                    <span>Get {storeName} coupons in your inbox</span>
                </p>
                <p className="sp-newsletter__hint">
                    We&apos;ll notify you when new verified codes are added.
                </p>
            </div>
            <form className="sp-newsletter__form" onSubmit={handleSubmit} noValidate={false}>
                <label htmlFor={inputId} className="sr-only">Email address</label>
                {mounted ? (
                    <input
                        id={inputId}
                        type="email"
                        name="email"
                        autoComplete="email"
                        inputMode="email"
                        required
                        data-lpignore="true"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email"
                        disabled={status === "loading" || status === "success"}
                        aria-describedby={message ? messageId : undefined}
                        className="sp-newsletter__input"
                    />
                ) : (
                    <div className="sp-newsletter__input" aria-hidden="true" />
                )}
                <button
                    type="submit"
                    disabled={status === "loading" || status === "success"}
                    className="sp-newsletter__button"
                >
                    {status === "loading" ? "..." : status === "success" ? "Subscribed!" : "Get Alerts"}
                </button>
            </form>
            {message && (
                <p id={messageId} role={status === "error" ? "alert" : "status"} className="sp-newsletter__message" data-status={status}>
                    {message}
                </p>
            )}
        </section>
    );
}
